import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useKeycloak } from '@react-keycloak/web';
import { DesignSystem, getTableHeaderStyle } from '../../styles/designSystem';
import { FaBox, FaShippingFast, FaCheckCircle, FaCog, FaMapMarkerAlt, FaSearch, FaPlus, FaChevronDown, FaCircle, FaUser, FaIndustry } from 'react-icons/fa';
import { BsGrid, BsListTask } from 'react-icons/bs';
import { ProjectAsset, getProjectAssets, updateProjectAsset, getProjectAssetStatuses } from '../../api/assets';
import { AddAssetModal } from './AddAssetModal';
import { StatusManager } from './StatusManager';
import { ManufacturerManager } from './ManufacturerManager';
import { EditWarrantyModal } from './EditWarrantyModal';
import { AssetDetailsModal } from './AssetDetailsModal';
import { EditAssetModal } from './EditAssetModal';
import { Avatar } from '../Avatar';
import { useNavigate } from 'react-router';

interface TabAssetsProps {
  projectId: string;
  token?: string;
}

type ViewStyle = 'kanban' | 'list';

// Helper function to get color for status
const getStatusColor = (statusName: string, isPhysicalOnly: boolean = false): string => {
  const colorMap: Record<string, string> = {
    'Planning to Purchase': '#6B7280',
    'Planned': '#6B7280',
    'Ordered': '#3B82F6',
    'Shipped': '#F59E0B',
    'In Warehouse': '#6366F1',
    'Delivered': '#8B5CF6',
    'Installed': isPhysicalOnly ? '#10B981' : '#059669', // Two types: Physical only (bright green) vs Workflow status (dark green)
    'In Use': '#059669',
    'Active': '#059669',
    'Deployed': '#059669'
  };
  return colorMap[statusName] || '#6B7280';
};

// Helper function to get display status for asset
const getAssetDisplayStatus = (asset: ProjectAsset) => {
  let status = 'Planning to Purchase';
  let color = getStatusColor('Planning to Purchase');
  let icon = <FaBox className="w-3 h-3" />;

  // PRIORITY 1: Use status from asset.status if available (WORKFLOW STATUS)
  // This means the asset has a proper workflow status assigned
  if (asset.status?.name) {
    status = asset.status.name;
    color = getStatusColor(asset.status.name, false); // Workflow status - use darker colors
    
    // Set appropriate icon based on status name
    switch (asset.status.name.toLowerCase()) {
      case 'installed':
      case 'in use':
      case 'active':
      case 'deployed':
        icon = <FaCheckCircle className="w-3 h-3" />;
        break;
      case 'shipped':
      case 'in transit':
        icon = <FaShippingFast className="w-3 h-3" />;
        break;
      case 'ordered':
      case 'planning to purchase':
      case 'in warehouse':
      case 'warehouse':
      default:
        icon = <FaBox className="w-3 h-3" />;
        break;
    }
    console.log(`🎨 Asset ${asset.name}: WORKFLOW STATUS="${asset.status.name}" (dark green #059669)`);
  } else {
    console.log(`🎨 Asset ${asset.name}: NO WORKFLOW STATUS, using physical state, is_installed=${asset.is_installed}`);
    // PRIORITY 2: Fallback logic based on physical asset state (PHYSICAL STATUS ONLY)
    // This means the asset was installed but no workflow status was set
    if (asset.is_installed) {
      status = 'Installed';
      color = getStatusColor('Installed', true); // Physical installation - use BRIGHTER green to distinguish
      icon = <FaCheckCircle className="w-3 h-3" />;
      console.log(`🎨 Asset ${asset.name}: PHYSICAL INSTALLATION ONLY (bright green #10B981)`);
    } else if (asset.tracking_number) {
      status = 'Shipped';
      color = getStatusColor('Shipped');
      icon = <FaShippingFast className="w-3 h-3" />;
    } else if (asset.ordered_at && new Date(asset.ordered_at) <= new Date()) {
      status = 'Ordered';
      color = getStatusColor('Ordered');
      icon = <FaBox className="w-3 h-3" />;
    }
  }

  return { status, color, icon };
};

// Draggable Asset Card Component - ORIGINAL DESIGN with HTML5 drag-and-drop
const DraggableAssetCard: React.FC<{ 
  asset: ProjectAsset; 
  onSelect: (asset: ProjectAsset) => void;
  displayStatus?: { status: string; color: string; icon: React.ReactNode };
  isDragging?: boolean;
  navigate?: (path: string) => void;
  projectId?: string;
}> = ({ asset, onSelect, displayStatus, isDragging = false, navigate, projectId: _projectId }) => {
  // Fallback to getAssetDisplayStatus if displayStatus is not provided
  const status = displayStatus || getAssetDisplayStatus(asset);

  const handleClick = () => {
    // Only trigger click if not dragging
    if (!isDragging) {
      onSelect(asset);
    }
  };

  const handleDragStart = (e: React.DragEvent) => {
    console.log('🚀 Drag start:', asset.name, 'ID:', asset.id);
    e.dataTransfer.setData('text/plain', asset.id);
    e.dataTransfer.effectAllowed = 'move';
  };
  
  return (
    <div
      draggable
      onDragStart={handleDragStart}
      onClick={handleClick}
      className="bg-white rounded-lg transition-all duration-200 hover:bg-gray-50 cursor-pointer relative"
      style={{ 
        border: `0.5px solid ${DesignSystem.colors.border.gray200}`,
        padding: '16px',
        fontFamily: 'Arcadia Text, system-ui, sans-serif',
        opacity: isDragging ? 0.5 : 1
      }}
    >
      <div className="flex justify-between items-start mb-3">
        <h4 
          className="font-medium text-sm leading-5"
          style={{ 
            color: DesignSystem.colors.primary.text,
            fontFamily: 'Arcadia Display, system-ui, sans-serif' 
          }}
        >
          {asset.name}
        </h4>
          <span 
          className="text-xs px-2 py-1 rounded-full text-white flex items-center gap-1"
          style={{ backgroundColor: status.color }}
        >
          {status.icon}
          {status.status}
          </span>
      </div>
      
      <div className="space-y-2 text-xs" style={{ color: DesignSystem.colors.primary.textSecondary }}>
        <div><strong>Item:</strong> {asset.item}</div>
        <div><strong>Quantity:</strong> {asset.quantity.toLocaleString()}</div>
        {/* Only show tracking after asset is ordered/shipped */}
        {asset.tracking_number && (status.status !== 'Planning to Purchase' && status.status !== 'Planned') && (
          <div><strong>Tracking:</strong> {asset.tracking_number}</div>
        )}
        <div><strong>ETA:</strong> {asset.eta ? new Date(asset.eta).toLocaleDateString() : 'TBD'}</div>
        
        {/* Location info for installed assets */}
        {asset.is_installed && asset.unit && (
          <div 
            className="mt-3 p-2 bg-green-50 rounded border border-green-200 cursor-pointer hover:bg-green-100 transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              if (asset.unit && asset.unit.site_id && asset.unit.floor_id && asset.unit.room_id && asset.unit.row_id && asset.unit.rack_id && navigate) {
                console.log('🎯 Direct rack navigation using unit fields:', {
                  site_id: asset.unit.site_id,
                  floor_id: asset.unit.floor_id,
                  room_id: asset.unit.room_id,
                  row_id: asset.unit.row_id,
                  rack_id: asset.unit.rack_id
                });
                
                // Construct URL using unit fields
                const rackUrl = `/projects/${asset.project_id}/sites/${asset.unit.site_id}/floors/${asset.unit.floor_id}/rooms/${asset.unit.room_id}/rows/${asset.unit.row_id}/racks/${asset.unit.rack_id}`;
                console.log('🎯 Full rack URL:', rackUrl);
                
                // Navigate directly
                navigate(rackUrl);
                toast.success('🚀 Opening rack view...');
              }
            }}
            title="Click to view rack"
          >
            <div className="flex items-center gap-1 text-green-800 mb-1">
              <FaMapMarkerAlt className="text-xs" />
              <strong className="text-xs">Installed Location:</strong>
            </div>
            <div className="text-xs text-green-700">
              <div><strong>Rack:</strong> {asset.rack?.slice(-8) || asset.unit?.rack_id?.slice(-8) || 'Unknown'}</div>
              <div><strong>Position:</strong> {asset.unit?.position}U</div>
              {(asset as any).current_responsible_person && (
                <div className="flex items-center gap-1 mt-1">
                  <Avatar
                    firstName={(asset as any).current_responsible_person.first_name}
                    lastName={(asset as any).current_responsible_person.last_name}
                    size={20}
                  />
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Warranty info */}
        {asset.warranty && (
          <div className="mt-2 text-xs">
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Warranty</span>
              <span className={asset.warranty.is_active_warranty ? 'text-green-600' : 'text-red-600'}>
                {asset.warranty.is_active_warranty ? 'Active' : 'Expired'}
              </span>
            </div>
            {asset.warranty.is_active_warranty && (
              <div className="text-gray-600 mt-1">
                {asset.warranty.days_remaining > 0 
                  ? `${asset.warranty.days_remaining} days remaining`
                  : 'Expires soon'
                }
              </div>
            )}
          </div>
        )}
        
        {/* User icon in bottom right corner */}
        {(asset as any).current_responsible_person && (
          <div className="absolute bottom-2 right-2">
            <FaUser className="w-4 h-4 text-gray-400" />
          </div>
        )}
      </div>
    </div>
  );
};

// Droppable Column Component  
const DroppableColumn: React.FC<{
  column: { id: string; title: string; color: string };
  children: React.ReactNode;
  onDrop: (assetId: string, columnId: string) => void;
}> = ({ column, children, onDrop }) => {
  const [isOver, setIsOver] = useState(false);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsOver(true);
  };

  const handleDragLeave = () => {
    setIsOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const assetId = e.dataTransfer.getData('text/plain');
    console.log('🎯 Drop:', assetId, 'to', column.id);
    setIsOver(false);
    onDrop(assetId, column.id);
  };

  return (
    <div 
      className={`flex-1 min-w-[300px] bg-gray-50 rounded-lg p-4 transition-colors ${
        isOver ? 'bg-blue-50 ring-2 ring-blue-200' : ''
      }`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <div className="flex items-center gap-2 mb-4">
        <div 
          className="w-3 h-3 rounded-full"
          style={{ backgroundColor: column.color }}
        />
        <h3 
          className="font-medium text-gray-900"
          style={{ fontFamily: 'Arcadia Display, system-ui, sans-serif' }}
        >
          {column.title}
        </h3>
      </div>
      
      <div className="space-y-3">
        {children}
      </div>
    </div>
  );
};

interface AssetRowProps {
  asset: ProjectAsset;
  onSelect: (asset: ProjectAsset) => void;
  onWarrantyEdit?: (asset: ProjectAsset) => void;
  navigate?: (path: string) => void;
  projectId?: string;
}

// Asset Row Component - styled like ActionItems
const AssetRow: React.FC<AssetRowProps> = ({ asset, onSelect, onWarrantyEdit, navigate, projectId: _projectId }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const displayStatus = getAssetDisplayStatus(asset);

  return (
    <div className="group hover:bg-gray-50/50 transition-all duration-200">
      {/* Main Row */}
      <div className="flex items-center px-6 py-4 relative" style={{ borderBottom: `0.5px solid ${DesignSystem.colors.border.gray200}` }}>
        {/* Clickable Area for Expand */}
        <div 
          className="absolute inset-0 cursor-pointer"
          onClick={() => setIsExpanded(!isExpanded)}
        />
        
        {/* Expand Button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded(!isExpanded);
          }}
          className="mr-4 p-1 rounded-md hover:bg-gray-100 transition-colors relative z-10"
        >
          <FaChevronDown className={`w-3 h-3 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
        </button>

        {/* Status Indicator */}
        <div className="mr-4 relative z-10 flex items-center space-x-2">
          <div className="relative">
            <FaCircle 
              className="w-2 h-2" 
              style={{ color: displayStatus.color }}
            />
          </div>
        </div>

        {/* Asset Name */}
        <div className="flex-1 min-w-0 relative z-10 flex items-center">
          <div className="flex-1">
            <div 
              className="font-medium text-sm"
              style={{ 
                color: DesignSystem.colors.primary.text,
                fontFamily: 'Arcadia Display, system-ui, sans-serif' 
              }}
            >
              {asset.name}
            </div>
            <div 
              className="text-xs mt-1"
              style={{ color: DesignSystem.colors.primary.textSecondary }}
            >
              {asset.item}
            </div>
          </div>
        </div>

        {/* Status */}
        <div className="mx-6 w-24 relative z-10">
          <span 
            className="text-xs px-2 py-1 rounded-full text-white flex items-center gap-1"
            style={{ backgroundColor: displayStatus.color }}
          >
            {displayStatus.icon}
            {displayStatus.status}
          </span>
        </div>

        {/* Quantity */}
        <div 
          className="mx-6 w-20 text-sm relative z-10"
          style={{ color: DesignSystem.colors.primary.text }}
        >
          {asset.quantity > 1000 ? `${Math.floor(asset.quantity/1000)}k+` : asset.quantity.toLocaleString()}
        </div>

        {/* Tracking */}
        <div 
          className="mx-6 w-24 text-sm relative z-10"
          style={{ color: DesignSystem.colors.primary.textSecondary }}
        >
          {/* Only show tracking after asset is ordered/shipped */}
          {(asset.tracking_number && displayStatus.status !== 'Planning to Purchase' && displayStatus.status !== 'Planned') 
            ? asset.tracking_number 
            : '-'
          }
        </div>

        {/* ETA */}
        <div 
          className="mx-6 w-24 text-sm relative z-10"
          style={{ color: DesignSystem.colors.primary.textSecondary }}
        >
          {asset.eta ? new Date(asset.eta).toLocaleDateString() : '-'}
        </div>

        {/* Actions */}
        <div className="ml-6 w-32 relative z-10">
          <div className="flex items-center space-x-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onSelect(asset);
              }}
              className="text-blue-600 hover:text-blue-800 text-sm"
            >
              View
            </button>
            {asset.warranty && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (onWarrantyEdit) {
                    onWarrantyEdit(asset);
                  }
                }}
                className="text-green-600 hover:text-green-800 text-sm"
              >
                Warranty
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Expanded Details */}
      {isExpanded && (
        <div className="px-6 pb-6 pt-2" style={{ 
          backgroundColor: DesignSystem.colors.background.gray50,
          borderTop: `0.5px solid ${DesignSystem.colors.border.gray200}` 
        }}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-4">
              <div className="text-sm">
                <div><strong>Description:</strong> {asset.description || 'N/A'}</div>
                <div><strong>Ordered:</strong> {asset.ordered_at ? new Date(asset.ordered_at).toLocaleDateString() : 'N/A'}</div>
                <div><strong>Lead Time:</strong> {asset.lead_time || 'N/A'} days</div>
              </div>
              
              {/* Location info for installed assets */}
              {asset.is_installed && (asset.unit || (asset.site && asset.floor && asset.room && asset.row && asset.rack)) && (
                <div 
                  className="p-3 bg-green-50 rounded border border-green-200 cursor-pointer hover:bg-green-100 transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (asset.unit && asset.unit.site_id && asset.unit.floor_id && asset.unit.room_id && asset.unit.row_id && asset.unit.rack_id && navigate) {
                      console.log('🎯 Direct rack navigation using unit fields (AssetRow):', {
                        site_id: asset.unit.site_id,
                        floor_id: asset.unit.floor_id,
                        room_id: asset.unit.room_id,
                        row_id: asset.unit.row_id,
                        rack_id: asset.unit.rack_id
                      });
                      
                      // Construct URL using unit fields
                      const rackUrl = `/projects/${asset.project_id}/sites/${asset.unit.site_id}/floors/${asset.unit.floor_id}/rooms/${asset.unit.room_id}/rows/${asset.unit.row_id}/racks/${asset.unit.rack_id}`;
                      console.log('🎯 Full rack URL:', rackUrl);
                      
                      // Navigate directly
                      navigate(rackUrl);
                      toast.success('🚀 Opening rack view...');
                    }
                  }}
                  title="Click to view rack"
                >
                  <div className="flex items-center gap-1 text-green-800 mb-2">
                    <FaMapMarkerAlt className="text-xs" />
                    <strong className="text-sm">Installed Location:</strong>
                  </div>
                  <div className="text-sm text-green-700">
                    <div><strong>Rack:</strong> {asset.rack?.slice(-8) || asset.unit?.rack_id?.slice(-8) || 'Unknown'}</div>
                    <div><strong>Position:</strong> {asset.unit?.position}U</div>
                    {(asset as any).current_responsible_person && (
                      <div className="flex items-center gap-2 mt-1">
                        <strong>Responsible:</strong>
                        <Avatar
                          firstName={(asset as any).current_responsible_person.first_name}
                          lastName={(asset as any).current_responsible_person.last_name}
                          size={24}
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Warranty info */}
            {asset.warranty && (
              <div className="space-y-4">
                <div className="text-sm">
                  <div><strong>Provider:</strong> {asset.warranty.warranty_provider}</div>
                  <div><strong>Model:</strong> {asset.warranty.model}</div>
                  <div><strong>Serial:</strong> {asset.warranty.serial_number}</div>
                  <div>
                    <strong>Status:</strong> 
                    <span className={`ml-2 px-2 py-1 text-xs rounded-full ${
              asset.warranty.is_active_warranty ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
            }`}>
                      {asset.warranty.is_active_warranty ? 'Active' : 'Expired'}
            </span>
          </div>
                  {asset.warranty.is_active_warranty && (
                    <div><strong>Days Remaining:</strong> {asset.warranty.days_remaining}</div>
        )}
      </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export const TabAssets: React.FC<TabAssetsProps> = ({ projectId, token }) => {
  const { keycloak } = useKeycloak();
  const authToken = token || keycloak?.token;
  const navigate = useNavigate();
  
  // Add cleanup flag to prevent DOM operations after unmount
  const [isUnmounted, setIsUnmounted] = useState(false);
  
  const [assets, setAssets] = useState<ProjectAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewStyle, setViewStyle] = useState<ViewStyle>('kanban');
  const [selectedAsset, setSelectedAsset] = useState<ProjectAsset | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showStatusManager, setShowStatusManager] = useState(false);
  const [showManufacturerManager, setShowManufacturerManager] = useState(false);
  const [showEditWarranty, setShowEditWarranty] = useState(false);
  const [showAssetDetails, setShowAssetDetails] = useState(false);
  const [showEditAsset, setShowEditAsset] = useState(false);
  const [selectedAssetForEdit, setSelectedAssetForEdit] = useState<ProjectAsset | null>(null);
  const [statuses, setStatuses] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isScrolled, setIsScrolled] = useState(false);

  // Scroll detection
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Load assets from API
  const loadAssets = async () => {
    console.log('🎬 loadAssets called with:', { authToken: !!authToken, projectId });
    if (!authToken || !projectId || isUnmounted) {
      console.log('❌ loadAssets aborted - missing authToken, projectId, or component unmounted');
      return;
    }
    
    setLoading(true);
    try {
      const response = await getProjectAssets(authToken, projectId, keycloak);
      console.log('🔍 Raw API Response:', response);
      
      const assetsArray = response.results || [];
      console.log('✅ Assets loaded:', assetsArray.length);
      
      // Debug: Log asset fields for installed assets
      const installedAssets = assetsArray.filter(asset => asset.is_installed);
      console.log('🔧 Installed assets:', installedAssets.length);
      installedAssets.forEach((asset, index) => {
        console.log(`🔧 Asset ${index + 1}:`, {
          id: asset.id,
          name: asset.name,
          is_installed: asset.is_installed,
          unit: asset.unit ? {
            site_id: asset.unit.site_id,
            floor_id: asset.unit.floor_id,
            room_id: asset.unit.room_id,
            row_id: asset.unit.row_id,
            rack_id: asset.unit.rack_id,
            position: asset.unit.position
          } : null,
          hasAllUnitFields: !!(asset.unit && asset.unit.site_id && asset.unit.floor_id && asset.unit.room_id && asset.unit.row_id && asset.unit.rack_id)
        });
      });
      
      if (!isUnmounted) {
        setAssets(assetsArray);
      }
    } catch (error) {
      console.error('Error loading assets:', error);
      if (!isUnmounted) {
        toast.error('Failed to load assets. Please try again later.');
        setAssets([]);
      }
    } finally {
      if (!isUnmounted) {
        setLoading(false);
      }
    }
  };

  // Load statuses
  const loadStatuses = async () => {
    if (!authToken || !projectId || isUnmounted) return;
    
    try {
      const response = await getProjectAssetStatuses(authToken, projectId, keycloak);
      console.log('📊 Loaded statuses:', response);
      if (!isUnmounted) {
        setStatuses(response.results || []);
      }
    } catch (error) {
      console.error('Error loading statuses:', error);
      if (!isUnmounted) {
        setStatuses([]);
      }
    }
  };

  useEffect(() => {
    if (!isUnmounted) {
      loadAssets();
      loadStatuses();
    }
  }, [projectId, authToken, isUnmounted]);

  // Cleanup effect to prevent DOM operations after unmount
  useEffect(() => {
    return () => {
      setIsUnmounted(true);
    };
  }, []);

  // Reload assets when user returns to this tab (from Rack page, etc.)
  useEffect(() => {
    if (isUnmounted) return;

    const handleFocus = () => {
      console.log('🔄 Tab Assets: Window focused, reloading assets...');
      if (authToken && projectId && !isUnmounted) {
        loadAssets();
      }
    };

    const handleVisibilityChange = () => {
      if (!document.hidden && authToken && projectId && !isUnmounted) {
        console.log('🔄 Tab Assets: Tab visible, reloading assets...');
        loadAssets();
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [authToken, projectId, isUnmounted]);

  // Handle asset selection for details view
  const handleAssetSelect = (asset: ProjectAsset) => {
    setSelectedAsset(asset);
    setShowAssetDetails(true);
  };

  // Filter assets by search query
  const filteredAssets = assets.filter(asset => {
    if (!searchQuery) return true;
    const searchLower = searchQuery.toLowerCase();
    return (
      asset.name?.toLowerCase().includes(searchLower) ||
      asset.item?.toLowerCase().includes(searchLower) ||
      asset.description?.toLowerCase().includes(searchLower) ||
      asset.tracking_number?.toLowerCase().includes(searchLower)
    );
  });

  // Group assets by status for Kanban view
  const defaultStatuses = [
    { id: 'Backlog', title: 'Backlog', color: '#6B7280' },
    { id: 'Planning to Purchase', title: 'Planning to Purchase', color: '#6B7280' },
    { id: 'Ordered', title: 'Ordered', color: '#3B82F6' },
    { id: 'Shipped', title: 'Shipped', color: '#F59E0B' },
    { id: 'In Warehouse', title: 'In Warehouse', color: '#6366F1' },
    { id: 'Delivered', title: 'Delivered', color: '#8B5CF6' },
    { id: 'Installed', title: 'Installed', color: '#059669' },
  ];

  // Always use statuses from API, fallback to default only if API fails
  const statusColumns = statuses.length > 0 
    ? statuses
        .sort((a, b) => a.status_order - b.status_order)
        .map(status => ({
          id: status.name,
          title: status.name,
          color: getStatusColor(status.name),
        }))
    : defaultStatuses;

  // Group assets by dynamic status
  const statusGroups = statusColumns.reduce((groups, column) => {
    const assetsInColumn = filteredAssets.filter(asset => {
      const displayStatus = getAssetDisplayStatus(asset);
      return displayStatus.status === column.id;
    });
    groups[column.id] = assetsInColumn;
    return groups;
  }, {} as Record<string, typeof filteredAssets>);

  // Handle HTML5 drop
  const handleDrop = async (assetId: string, targetColumnId: string) => {
    if (!authToken) return;

    console.log('🎯 Drop: finding asset with ID:', assetId);
    
    const asset = filteredAssets.find(a => a.id === assetId);
    if (!asset) {
      console.log('❌ Asset not found with ID:', assetId);
      return;
    }

    // Check if it's actually a different column
    const currentStatus = getAssetDisplayStatus(asset).status;
    if (currentStatus === targetColumnId) {
      console.log('ℹ️ Asset already in target column');
      return;
      }

    // Find the status ID for the new status
    const newStatusId = statuses.find(s => s.name === targetColumnId)?.id;
    
    console.log('🔍 Status change:', currentStatus, '→', targetColumnId, 'Status ID:', newStatusId);
      
    // Update asset status optimistically
    const updatedAsset = updateAssetForStatus(asset, targetColumnId);
      setAssets(prevAssets => 
      prevAssets.map(a => a.id === asset.id ? updatedAsset : a)
      );
      
    toast.info(`Moving "${asset.name}" to "${targetColumnId}"...`);
      
      try {
      console.log('🆔 Using asset.id for API update:', asset.id);
      
      // Save to API (send ALL required fields including id in body)
      const updateData: any = {
        id: asset.id,  // API requires id in request body!
          project_id: projectId,
        item: asset.item,
        name: asset.name,
        description: asset.description,
        quantity: asset.quantity,
        ordered_at: asset.ordered_at,
        eta: asset.eta,
        tracking_number: asset.tracking_number,
        lead_time: asset.lead_time,
        esd: asset.esd,
        isd: asset.isd,
        unit_id: asset.unit_id,
        current_responsible_person_id: asset.current_responsible_person_id,
        is_installed: asset.is_installed,
        notes: asset.notes
      };

      if (newStatusId) {
        updateData.current_status_id = newStatusId;
      }

      console.log('📤 Sending to API:', JSON.stringify(updateData, null, 2));
      console.log('🔗 API URL will be:', `projects/${projectId}/assets/${asset.id}`);

      await updateProjectAsset(authToken, projectId, asset.id, updateData, keycloak);
        
      // Update local state by merging API response with existing asset to preserve all fields
        setAssets(prevAssets => 
        prevAssets.map(a => a.id === asset.id ? {
          ...a, // Keep all existing fields (warranty, status, unit, etc.)
          ...updatedAsset, // Apply optimistic changes
          current_status_id: newStatusId // Ensure status ID is updated
        } : a)
      );
      
      toast.success(`✅ "${asset.name}" moved to "${targetColumnId}"`);
      console.log('✅ Asset updated successfully');
    } catch (error) {
      console.error('❌ Failed to update asset:', error);
      // Revert on error
      setAssets(prevAssets => 
        prevAssets.map(a => a.id === asset.id ? asset : a)
        );
      toast.error(`Failed to move "${asset.name}": ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  // Helper function to update asset properties based on new status
  const updateAssetForStatus = (asset: ProjectAsset, newStatus: string): ProjectAsset => {
    const now = new Date().toISOString();
    const futureDate = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();
    
    const updatedStatus = {
      ...(asset.status || {}),
      name: newStatus
    };
    
    switch (newStatus) {
      case 'Backlog':
        return {
          ...asset,
          tracking_number: null,
          is_installed: false,
          status: updatedStatus,
          warranty: asset.warranty ? { ...asset.warranty, is_active_warranty: false } : null
        };
      case 'Planning to Purchase':
        return {
          ...asset,
          ordered_at: futureDate,
          eta: futureDate,
          tracking_number: null,
          is_installed: false,
          status: updatedStatus,
          warranty: asset.warranty ? { ...asset.warranty, is_active_warranty: false } : null
        };
      case 'Ordered':
        return {
          ...asset,
          ordered_at: now,
          eta: futureDate,
          tracking_number: null,
          is_installed: false,
          status: updatedStatus,
          warranty: asset.warranty ? { ...asset.warranty, is_active_warranty: false } : null
        };
      case 'Shipped':
        return {
          ...asset,
          ordered_at: now,
          eta: futureDate,
          tracking_number: asset.tracking_number || `TRK${Math.random().toString(36).substring(2, 8).toUpperCase()}`,
          is_installed: false,
          status: updatedStatus,
          warranty: asset.warranty ? { ...asset.warranty, is_active_warranty: false } : null
        };
      case 'In Warehouse':
        return {
          ...asset,
          ordered_at: now,
          eta: now,
          tracking_number: asset.tracking_number || `TRK${Math.random().toString(36).substring(2, 8).toUpperCase()}`,
          is_installed: false,
          status: updatedStatus,
          warranty: asset.warranty ? { ...asset.warranty, is_active_warranty: false } : null
        };
      case 'Delivered':
        return {
          ...asset,
          ordered_at: now,
          eta: now,
          tracking_number: asset.tracking_number || `TRK${Math.random().toString(36).substring(2, 8).toUpperCase()}`,
          is_installed: false,
          status: updatedStatus,
          warranty: asset.warranty ? { ...asset.warranty, is_active_warranty: false } : null
        };
      case 'Installed':
        return {
          ...asset,
          ordered_at: now,
          eta: now,
          is_installed: true,
          status: updatedStatus,
          warranty: asset.warranty ? { ...asset.warranty, is_active_warranty: true } : null
        };
      default:
        return { ...asset, status: updatedStatus };
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p style={{ color: DesignSystem.colors.primary.textSecondary, fontFamily: 'Arcadia Text, system-ui, sans-serif' }}>
            Loading assets...
          </p>
        </div>
    </div>
  );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header with Search, Actions and Table Header - Fixed Sticky Section */}
      <div className="sticky top-0 z-30">
        <div className="max-w-[1280px] mx-auto px-0 py-4">
          <div className={`bg-white rounded-lg transition-shadow duration-200 ${isScrolled ? 'shadow-md' : ''}`} style={{ border: `0.5px solid ${DesignSystem.colors.border.gray200}` }}>
            {/* Search and Actions Row */}
            <div className="px-6 py-4" style={{ borderBottom: '0.5px solid #E5E7EB' }}>
              <div className="flex items-center justify-between">
                {/* Left side - Search and Quick Stats */}
                <div className="flex items-center space-x-6">
                  <div className="relative">
                    <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      placeholder="Search assets..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 pr-4 py-2.5 w-80 text-sm bg-gray-50 border-0 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all"
                    />
        </div>
                  
                  {/* Quick Stats */}
                  <div className="flex items-center space-x-4 text-sm">
                    <span className="text-gray-500">
                      {filteredAssets.length} of {assets.length} assets
                    </span>
                    <div className="w-px h-4 bg-gray-300"></div>
                    <span className="text-gray-500">
                      {filteredAssets.filter(asset => getAssetDisplayStatus(asset).status === 'Installed').length} installed
                    </span>
                  </div>
                </div>

                {/* Right side - Actions */}
                <div className="flex items-center space-x-4">
          {/* View Style Toggle */}
                  <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewStyle('kanban')}
                      className={`px-3 py-1.5 text-sm rounded-md transition-all flex items-center gap-2 ${
                viewStyle === 'kanban' 
                          ? 'bg-white text-blue-600 shadow-sm' 
                          : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
                      <BsGrid className="w-3.5 h-3.5" />
                      Board
            </button>
            <button
              onClick={() => setViewStyle('list')}
                      className={`px-3 py-1.5 text-sm rounded-md transition-all flex items-center gap-2 ${
                viewStyle === 'list' 
                          ? 'bg-white text-blue-600 shadow-sm' 
                          : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
                      <BsListTask className="w-3.5 h-3.5" />
              List
            </button>
          </div>

                  {/* Statuses */}
          <button
            onClick={() => setShowStatusManager(true)}
                    className="flex items-center space-x-2 bg-transparent text-gray-400 border border-gray-200 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 hover:border-gray-300 transition-colors"
          >
                    <FaCog className="w-3.5 h-3.5" />
                    <span>Statuses</span>
          </button>

                  {/* Manufacturers */}
          <button
            onClick={() => setShowManufacturerManager(true)}
                    className="flex items-center space-x-2 bg-transparent text-gray-400 border border-gray-200 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 hover:border-gray-300 transition-colors"
          >
                    <FaIndustry className="w-3.5 h-3.5" />
                    <span>Manufacturers</span>
          </button>

                  {/* Add Asset */}
          <button
            onClick={() => setShowAddModal(true)}
                    className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors shadow-sm"
          >
                    <FaPlus className="w-3.5 h-3.5" />
                    <span>Add Asset</span>
          </button>
                </div>
        </div>
      </div>

            {/* Description Bar */}
            <div className="px-6 py-2">
              <div className="flex items-center text-xs font-medium" style={getTableHeaderStyle()}>
                <div className="flex-1">
                  Track materials and equipment through the procurement and installation process
                </div>
              </div>
            </div>

            {/* List View Table Header */}
            {viewStyle === 'list' && (
              <div className="px-6 py-2">
                <div className="flex items-center text-xs font-medium" style={getTableHeaderStyle()}>
                  <div className="w-8"></div> {/* Expand button space */}
                  <div className="w-4 mr-4"></div> {/* Status indicator space */}
                  <div className="flex-1" role="columnheader">Asset</div>
                  <div className="mx-6 w-24" role="columnheader">Status</div>
                  <div className="mx-6 w-20" role="columnheader">Quantity</div>
                  <div className="mx-6 w-24" role="columnheader">Tracking</div>
                  <div className="mx-6 w-24" role="columnheader">ETA</div>
                  <div className="ml-6 w-32" role="columnheader">Actions</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="max-w-[1280px] mx-auto px-0 -mt-4">
        <div className="bg-white">
      {/* Empty State */}
          {filteredAssets.length === 0 && !loading && (
            <div className="text-center py-16">
          <FaBox className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 
            className="text-lg font-medium text-gray-900 mb-2"
            style={{ fontFamily: 'Arcadia Display, system-ui, sans-serif' }}
          >
            No Assets Found
          </h3>
          <p 
            className="text-gray-600 mb-6"
            style={{ fontFamily: 'Arcadia Text, system-ui, sans-serif' }}
          >
                {searchQuery 
                  ? 'No assets match your search criteria. Try adjusting your search.'
                  : 'Get started by adding your first project asset to track materials and equipment.'
                }
          </p>
          <button
            onClick={() => setShowAddModal(true)}
                className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors shadow-sm mx-auto"
          >
                <FaPlus className="w-3.5 h-3.5" />
                <span>Add First Asset</span>
          </button>
        </div>
      )}

          {/* Kanban View */}
          {viewStyle === 'kanban' && filteredAssets.length > 0 && (
            <div className="p-6">
              <div className="flex gap-6 overflow-x-auto pb-6">
              {statusColumns.map(column => (
                <DroppableColumn
                key={column.id}
                  column={column}
                    onDrop={handleDrop}
                  >
                    {statusGroups[column.id]?.map(asset => (
                                            <DraggableAssetCard
                        key={asset.id}
                        asset={asset}
                  onSelect={handleAssetSelect}
                        displayStatus={getAssetDisplayStatus(asset)}
                        navigate={navigate}
                        projectId={projectId}
                />
                    ))}
                  </DroppableColumn>
                  ))}
                </div>
          </div>
      )}

      {/* List View */}
          {viewStyle === 'list' && filteredAssets.length > 0 && (
          <div>
              {/* Assets List */}
              <div>
                {filteredAssets.map(asset => (
                                    <AssetRow 
                key={asset.id}
                    asset={asset}
                    onSelect={handleAssetSelect}
                    onWarrantyEdit={() => {
                      setSelectedAsset(asset);
                      setShowEditWarranty(true);
                    }}
                    navigate={navigate}
                    projectId={projectId}
                  />
                ))}
                      </div>
                  </div>
                      )}
                    </div>
                  </div>

      {/* Modals */}
      {showAddModal && (
        <AddAssetModal
          projectId={projectId}
          onClose={() => setShowAddModal(false)}
          onAssetAdded={() => {
            loadAssets();
            setShowAddModal(false);
          }}
        />
      )}

       <StatusManager
         projectId={projectId}
         isOpen={showStatusManager}
         onClose={() => setShowStatusManager(false)}
        onStatusesChanged={() => {
          loadStatuses();
          loadAssets();
        }}
       />

       <ManufacturerManager
         isOpen={showManufacturerManager}
         onClose={() => setShowManufacturerManager(false)}
         onManufacturersChanged={() => {
           loadAssets();
         }}
       />

       {selectedAsset && (
         <EditWarrantyModal
           projectId={projectId}
           assetId={selectedAsset.id}
           warranty={selectedAsset.warranty}
           isOpen={showEditWarranty}
           onClose={() => setShowEditWarranty(false)}
           onWarrantyUpdated={() => {
             loadAssets();
             setShowEditWarranty(false);
           }}
         />
       )}

       {selectedAsset && (
         <AssetDetailsModal
           asset={selectedAsset}
           projectId={projectId}
           isOpen={showAssetDetails}
           onClose={() => {
             setShowAssetDetails(false);
             setSelectedAsset(null);
           }}
           onEdit={(updatedAsset) => {
             // Update the selected asset with the updated data
             setSelectedAsset(updatedAsset);
             // Reload assets to reflect changes
             loadAssets();
           }}
           onWarrantyEdit={() => {
             setShowAssetDetails(false);
             setShowEditWarranty(true);
           }}
         />
       )}

       {selectedAssetForEdit && (
         <EditAssetModal
           asset={selectedAssetForEdit}
           projectId={projectId}
           isOpen={showEditAsset}
           onClose={() => {
             setShowEditAsset(false);
             setSelectedAssetForEdit(null);
           }}
           onAssetUpdated={() => {
             loadAssets();
             setShowEditAsset(false);
             setSelectedAssetForEdit(null);
           }}
         />
       )}
    </div>
  );
}; 